import React, { useEffect, useState, useCallback } from "react";
import { getAllPhotosByCompetitionId } from "../../services/photosServices";
import {
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaCamera,
} from "react-icons/fa";

const PublicPhotoGallery = ({ competitionId }) => {
  const [photos, setPhotos] = useState([]);
  const [enlargedIndex, setEnlargedIndex] = useState(null);
  const [galleryPage, setGalleryPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(4);

  // ⚙️ Adaptation responsive
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(4);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const data = await getAllPhotosByCompetitionId(competitionId);
        setPhotos(data);
        setGalleryPage(0); // Reset à la première page
      } catch (err) {
        console.error("Erreur chargement photos:", err);
      }
    };
    if (competitionId) fetchPhotos();
  }, [competitionId]);

  const handleKeyDown = useCallback(
    (e) => {
      if (enlargedIndex === null) return;

      if (e.key === "ArrowRight") {
        setEnlargedIndex((prev) => (prev + 1) % photos.length);
      } else if (e.key === "ArrowLeft") {
        setEnlargedIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
      } else if (e.key === "Escape") {
        setEnlargedIndex(null);
      }
    },
    [enlargedIndex, photos.length]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (photos.length === 0) return null;

  const totalPages = Math.ceil(photos.length / itemsPerPage);
  const currentPhotos = photos.slice(
    galleryPage * itemsPerPage,
    galleryPage * itemsPerPage + itemsPerPage
  );

  return (
    <div className="max-w-[1280px] mx-auto mt-12 px-4 mb-6">
      <h2 className="text-2xl font-bold mb-4 text-center text-gray-800 dark:text-white flex items-center justify-center gap-2">
        <FaCamera className="text-primary text-3xl" />
        Photos des Podiums
      </h2>

      {/* Vignettes paginées */}
      <div className="flex items-center justify-center gap-4">
        {/* Chevron précédent */}
        <button
          disabled={galleryPage === 0}
          onClick={() => setGalleryPage((prev) => prev - 1)}
          className="text-2xl text-gray-600 dark:text-white hover:text-primary disabled:opacity-30"
        >
          <FaChevronLeft />
        </button>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-[800px]">
          {currentPhotos.map((photo, index) => {
            const globalIndex = galleryPage * itemsPerPage + index;
            return (
              <div key={photo.id} className="cursor-pointer">
                <img
                  src={photo.photo_url}
                  alt={photo.description}
                  onClick={() => setEnlargedIndex(globalIndex)}
                  className="rounded-lg shadow-md hover:scale-105 transition-transform duration-300 object-cover h-40 w-full"
                />
                {photo.description && (
                  <p className="text-xs text-center mt-1 text-gray-600 dark:text-gray-300">
                    {photo.description}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Chevron suivant */}
        <button
          disabled={galleryPage >= totalPages - 1}
          onClick={() => setGalleryPage((prev) => prev + 1)}
          className="text-2xl text-gray-600 dark:text-white hover:text-primary disabled:opacity-30"
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Lightbox agrandie */}
      {enlargedIndex !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          onClick={() => setEnlargedIndex(null)}
        >
          <div
            className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setEnlargedIndex(null)}
              className="absolute top-4 right-4 text-white text-xl hover:text-red-400"
            >
              <FaTimes />
            </button>

            <button
              onClick={() =>
                setEnlargedIndex(
                  enlargedIndex === 0 ? photos.length - 1 : enlargedIndex - 1
                )
              }
              className="absolute left-4 text-white text-3xl hover:text-blue-400"
            >
              <FaChevronLeft />
            </button>

            <img
              src={photos[enlargedIndex].photo_url}
              alt={photos[enlargedIndex].description}
              className="rounded-lg shadow-lg max-h-[80vh] max-w-full object-contain"
            />

            <button
              onClick={() =>
                setEnlargedIndex((enlargedIndex + 1) % photos.length)
              }
              className="absolute right-4 text-white text-3xl hover:text-blue-400"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicPhotoGallery;
